import {
  AgreementType,
  LendingPaymentSource,
} from '@varo/graphql-schema-types';
import { formatISODate } from 'features/advance/helpers/Advance.helpers';
import useAgreementsForDisbursement from 'features/advance/hooks/review/useGenerateAgreementsForDisbursement';
import useBackupPayments from 'features/advance/hooks/useBackupPayments';
import { useCreateReviewListItems } from 'features/advance/hooks/useCreateReviewListItems';
import { useEffect } from 'react';

interface ReviewScreenOnRenderQueriesParams {
  dueDate: string;
  feeAmount: string;
  advanceAmount: string;
  repaymentDate: string;
  selectedAccountName: string | undefined;
  totalRepaymentAmountWithFee: string;
  paymentSource?: LendingPaymentSource;
  selectedAccountId?: string;
}

const useReviewScreenOnRenderQueries = ({
  dueDate,
  feeAmount,
  advanceAmount,
  repaymentDate,
  selectedAccountName,
  totalRepaymentAmountWithFee,
  paymentSource,
  selectedAccountId,
}: ReviewScreenOnRenderQueriesParams) => {
  const {
    data: agreementsForDisbursementResponse,
    fetching: fetchingAgreementsForDisbursement,
    error: agreementsForDisbursementError,
    execute: executeAgreementsForDisbursementMutation,
  } = useAgreementsForDisbursement();

  const {
    result: backupPaymentMethodsResponse,
    fetching: fetchingBackupPayments,
    error: fetchingBackupPaymentsError,
  } = useBackupPayments();

  const formattedRepaymentDate = formatISODate(repaymentDate, 'MMM dd, yyyy');

  const { reviewListItems } = useCreateReviewListItems({
    dueDate,
    feeAmount,
    advanceAmount,
    repaymentDate: formattedRepaymentDate,
    autoPaymentMethod: selectedAccountName,
  });

  useEffect(() => {
    if (dueDate && totalRepaymentAmountWithFee) {
      return void executeAgreementsForDisbursementMutation({
        input: {
          amount: {
            amount: totalRepaymentAmountWithFee,
            currency: 'USD',
          },
        },
      });
    }
  }, [
    dueDate,
    executeAgreementsForDisbursementMutation,
    paymentSource,
    repaymentDate,
    selectedAccountId,
    totalRepaymentAmountWithFee,
  ]);

  const agreementIds = agreementsForDisbursementResponse?.map(
    (agreement) => agreement.id,
  );

  const filteredAgreements = agreementsForDisbursementResponse?.filter(
    (agreement) => agreement.type !== AgreementType.AdvanceAgreement,
  );

  return {
    data: {
      backupPaymentMethodsResponse,
      formattedRepaymentDate,
      reviewListItems,
      agreementIds,
      dynamicAgreements: filteredAgreements,
    },
    isFetching: fetchingBackupPayments || fetchingAgreementsForDisbursement,
    hasError: fetchingBackupPaymentsError || agreementsForDisbursementError,
  };
};

export default useReviewScreenOnRenderQueries;
